import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, ShieldCheck, Loader2, CheckCircle2, CreditCard, ArrowRight } from "lucide-react";
import { CheckoutShell, Field } from "./checkout/CheckoutShell";
import { TurnstileField } from "../components/TurnstileField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAPTCHA_ON = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Edits() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [requests, setRequests] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState("");
  const [result, setResult] = useState(null); // { outcome, plan_active, request_count, payment_link }

  const cleaned = requests.map((r) => r.trim()).filter(Boolean);
  const count = cleaned.length;

  const addRequest = () => setRequests([...requests, ""]);
  const removeRequest = (i) => setRequests(requests.filter((_, idx) => idx !== i));
  const updateRequest = (i, val) => setRequests(requests.map((r, idx) => (idx === i ? val : r)));

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and the email on your plan.");
      return;
    }
    if (count === 0) {
      toast.error("Please describe at least one edit you need.");
      return;
    }
    if (CAPTCHA_ON && !token) {
      toast.error("Please complete the verification below.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/edits/submit`, {
        name,
        email,
        website_url: websiteUrl,
        requests: cleaned,
        turnstile_token: token,
      }, { timeout: 30000 });
      setResult(data);
      if (data.outcome === "pending_approval") {
        toast.success("Request received — we'll confirm your maintenance plan and get started shortly.");
      } else if (data.reason === "over_limit") {
        toast.info("You've used your 3 included edits this month — additional edits are $25 each.");
      } else {
        toast.info("No active plan found — a $50 payment is required per request.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setRequests([""]);
    setToken("");
  };

  // ---------- Result: active plan (pending confirmation) ----------
  if (result?.outcome === "pending_approval") {
    return (
      <CheckoutShell title="Request received" subtitle="You're on a maintenance plan — no payment needed.">
        <div
          data-testid="edits-active-result"
          className="rounded-[2rem] bg-white border border-gray-100 p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.05)] text-center"
        >
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
            <CheckCircle2 className="text-green-600" size={34} />
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-charcoal mt-6">
            Thanks — we've got it
          </h3>
          <p className="font-body text-gray-500 mt-4 max-w-lg mx-auto">
            We've received your {result.request_count} edit request{result.request_count !== 1 ? "s" : ""}. Since
            you're on a maintenance plan, there's <span className="text-charcoal font-semibold">no charge</span>.
            Our team will confirm your plan and get started shortly — we'll email you as soon as it's underway.
          </p>
          <button
            type="button"
            data-testid="edits-another-button"
            onClick={resetForm}
            className="font-display mt-8 inline-flex items-center gap-2 bg-charcoal text-white hover:bg-sky-brand hover:text-charcoal transition-colors duration-300 font-semibold px-8 py-4 rounded-full"
          >
            Submit another request
          </button>
        </div>
      </CheckoutShell>
    );
  }

  // ---------- Result: payment required ($50 no-plan, or $25 monthly overage) ----------
  if (result && result.outcome === "payment_required") {
    const overLimit = result.reason === "over_limit";
    const price = result.unit_price || (overLimit ? 25 : 50);
    return (
      <CheckoutShell
        title="Almost there"
        subtitle={
          overLimit
            ? "You've used your included edits for this month."
            : "We couldn't find an active maintenance plan for that email."
        }
      >
        <div
          data-testid="edits-payment-result"
          className="rounded-[2rem] bg-white border border-gray-100 p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.05)] text-center"
        >
          <div className="w-16 h-16 rounded-full bg-sky-brand/15 flex items-center justify-center mx-auto">
            <CreditCard className="text-sky-brand" size={32} />
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-charcoal mt-6">
            {overLimit ? "Monthly edits used up" : "No active plan found"}
          </h3>
          <p className="font-body text-gray-500 mt-4 max-w-lg mx-auto">
            {overLimit ? (
              <>
                Your plan includes <span className="text-charcoal font-semibold">3 edits each month</span>, and
                you've used them for now. Additional edits are just{" "}
                <span className="text-charcoal font-semibold">$25 each</span>. We've saved your{" "}
                {result.request_count} request{result.request_count !== 1 ? "s" : ""}; complete payment below and
                we'll begin. Your included edits reset on the 1st.
              </>
            ) : (
              <>
                No worries — site edits are{" "}
                <span className="text-charcoal font-semibold">$50 per request</span> for clients without an active
                maintenance plan. We've saved your {result.request_count} request
                {result.request_count !== 1 ? "s" : ""}; complete your payment below and we'll begin.
              </>
            )}
          </p>
          <a
            href={result.payment_link}
            data-testid="edits-pay-button"
            className="font-display mt-8 inline-flex items-center gap-2 bg-sky-brand text-charcoal hover:bg-charcoal hover:text-white transition-colors duration-300 font-semibold px-8 py-4 rounded-full"
          >
            {overLimit ? `Pay $${price} per additional edit` : `Pay $${price} per request`} <ArrowRight size={18} />
          </a>
          <div className="mt-6">
            <button
              type="button"
              data-testid="edits-back-button"
              onClick={resetForm}
              className="font-display text-sm text-gray-400 hover:text-charcoal transition-colors"
            >
              Use a different email
            </button>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  // ---------- Form ----------
  return (
    <CheckoutShell
      title="Request a Site Edit"
      subtitle="Enter the email on your maintenance plan and we'll verify it. Active plans are free — otherwise it's $50 per request."
    >
      <div className="max-w-2xl space-y-8" data-testid="edits-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field
            label="Your name *"
            data-testid="edits-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
          />
          <Field
            label="Plan email *"
            type="email"
            data-testid="edits-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@business.com"
          />
        </div>
        <Field
          label="Website URL"
          data-testid="edits-url-input"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://yoursite.com"
        />

        <div>
          <label className="font-display text-sm font-medium text-charcoal block mb-3">
            What edits do you need?
          </label>
          <div className="space-y-3">
            {requests.map((r, i) => (
              <div key={i} className="flex items-start gap-2" data-testid={`edits-request-row-${i}`}>
                <span className="font-display text-sky-brand font-bold mt-4 w-6 shrink-0">{i + 1}.</span>
                <textarea
                  data-testid={`edits-request-input-${i}`}
                  value={r}
                  onChange={(e) => updateRequest(i, e.target.value)}
                  rows={2}
                  placeholder={
                    i === 0
                      ? 'e.g. "Update the phone number in the footer and swap the hero image"'
                      : "Another separate change..."
                  }
                  className="flex-1 bg-gray-50 border border-gray-200 focus:border-sky-brand focus:ring-2 focus:ring-sky-brand/20 outline-none px-5 py-3 rounded-2xl font-body text-charcoal placeholder:text-gray-400 resize-none"
                />
                {requests.length > 1 && (
                  <button
                    type="button"
                    data-testid={`edits-remove-${i}`}
                    onClick={() => removeRequest(i)}
                    className="mt-3 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Remove request"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            data-testid="edits-add-request"
            onClick={addRequest}
            className="font-display mt-4 inline-flex items-center gap-2 text-sky-brand hover:text-charcoal font-semibold text-sm transition-colors"
          >
            <Plus size={18} /> Add request
          </button>
        </div>

        <TurnstileField onToken={setToken} />

        <button
          type="button"
          data-testid="edits-submit-button"
          onClick={handleSubmit}
          disabled={submitting || (CAPTCHA_ON && !token)}
          className="font-display w-full bg-sky-brand text-charcoal hover:bg-charcoal hover:text-white transition-colors duration-300 font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {submitting ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            <>
              <ShieldCheck size={18} /> Verify plan &amp; submit
            </>
          )}
        </button>
      </div>
    </CheckoutShell>
  );
}
